import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { s as scrollIntoView, g as getOffset } from './scroll.js';
import { f as focusVisible } from './focus-visible.js';
import { a as extractElementsFromSlot } from './slot-utils.js';
import { h as hasSlot } from './slot.js';
import { d as defineCustomElement$3 } from './icon.js';
import { d as defineCustomElement$2 } from './icon-button.js';

const tabGroupCss = ":host{position:relative;box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{--tabs-border-color:transparent;display:block}.tab-group{position:relative;display:flex;border:solid 1px transparent;height:100%}.tab-group .tab-group__tabs{display:flex;position:relative}.tab-group .tab-group__active-tab-indicator{position:absolute;transition:var(--tt-transition-fast) transform ease, var(--tt-transition-fast) width ease}.tab-group:not(.focus-visible) ::slotted(tt-tab){--focus-ring:none}.tab-group .tab-group-border{position:absolute;bottom:0;left:0;height:1px;width:100%;border-bottom:solid 1px var(--tt-color-gray-300);z-index:0}.tab-group.tab-subtabs{margin-top:-20px}.tab-group.tab-subtabs .tab-group-border{display:none}.tab-group .tab-group__body{height:100%;overflow:auto}.tab-group .tab-group__body::-webkit-scrollbar{height:0.5rem;width:0.5rem}.tab-group .tab-group__body::-webkit-scrollbar-track{background-color:var(--tt-color-gray-100);border-radius:var(--tt-border-radius-pill);border:0.125rem solid transparent;background-clip:content-box}.tab-group .tab-group__body::-webkit-scrollbar-thumb{background-color:var(--tt-color-gray-400);border-radius:var(--tt-border-radius-pill);border:0.125rem solid transparent;background-clip:content-box}.tab-group .tab-group__body:hover::-webkit-scrollbar-thumb{background-color:var(--tt-color-gray-500)}.tab-group .tab-group__body:hover::-webkit-scrollbar-thumb:hover{background-color:var(--tt-color-gray-700)}.tab-group .tab-group__body:hover::-webkit-scrollbar-track,.tab-group .tab-group__body:hover::-webkit-scrollbar-thumb{background-clip:border-box}.tab-group__nav-container{position:relative}.tab-group--has-scroll-controls .tab-group__nav-container{position:relative;padding:0 var(--tt-spacing-xl)}.tab-group__scroll-button{display:flex;align-items:center;justify-content:center;position:absolute;top:0;bottom:0;width:var(--tt-spacing-xl)}.tab-group__scroll-button--left{left:0}.tab-group__scroll-button--right{right:0}.tab-group__content{display:flex;align-items:center}.tab-group__content .tab-group__suffix,.tab-group__content .tab-group__prefix{flex:0 1 auto;display:none;padding-top:2px}.tab-group__content .tab-group__nav{flex:1 1 auto}.has-prefix .tab-group__content .tab-group__prefix,.has-suffix .tab-group__content .tab-group__suffix{display:inline-flex;border-bottom:4px solid transparent}.tab-group--top{flex-direction:column}.tab-group--top .tab-group__nav-container{order:1}.tab-group--top .tab-group__nav{display:flex;overflow-x:auto;scrollbar-width:none;-ms-overflow-style:none}.tab-group--top .tab-group__nav::-webkit-scrollbar{width:0;height:0}.tab-group--top .tab-group__tabs{flex:1 1 auto;flex-direction:row;border-bottom:solid 2px var(--tabs-border-color);z-index:10}.tab-group--top .tab-group__active-tab-indicator{bottom:-2px;border-bottom:solid 2px var(--tt-color-gray-900)}.tab-group--top .tab-group__body{order:2}.tab-group--bottom{flex-direction:column}.tab-group--bottom .tab-group__nav-container{order:2}.tab-group--bottom .tab-group__nav{display:flex;overflow-x:auto;scrollbar-width:none;-ms-overflow-style:none}.tab-group--bottom .tab-group__nav::-webkit-scrollbar{width:0;height:0}.tab-group--bottom .tab-group__tabs{flex:1 1 auto;position:relative;flex-direction:row;border-top:solid 2px var(--tabs-border-color);gap:var(--tt-spacing-xl)}.tab-group--bottom .tab-group__active-tab-indicator{top:-2px;border-top:solid 2px var(--tt-color-green-500)}.tab-group--bottom .tab-group__body{order:1}.tab-group--left{flex-direction:row}.tab-group--left .tab-group__nav-container{order:1}.tab-group--left .tab-group__tabs{flex:0 0 auto;flex-direction:column;border-right:solid 2px var(--tabs-border-color)}.tab-group--left .tab-group__active-tab-indicator{right:-2px;border-right:solid 2px var(--tt-color-green-500)}.tab-group--left .tab-group__body{flex:1 1 auto;order:2}.tab-group--right{flex-direction:row}.tab-group--right .tab-group__nav-container{order:2}.tab-group--right .tab-group__tabs{flex:0 0 auto;flex-direction:column;border-left:solid 2px var(--tabs-border-color)}.tab-group--right .tab-group__active-tab-indicator{left:-2px;border-left:solid 2px var(--tt-color-green-500)}.tab-group--right .tab-group__body{flex:1 1 auto;order:1}";

const TabGroup = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.slTabShow = createEvent(this, "tt-tab-show", 7);
    this.slTabHide = createEvent(this, "tt-tab-hide", 7);
    this.hasScrollControls = false;
    this.hasPrefix = false;
    this.hasSuffix = false;
    this.placement = 'top';
    this.subtabs = false;
    this.currentTab = undefined;
    this.noScrollControls = false;
  }
  handlePlacementChange() {
    this.syncActiveTabIndicator();
  }
  handleNoScrollControlsChange() {
    this.updateScrollControls();
  }
  handleSlotChange() {
    this.hasPrefix = hasSlot(this.element, 'prefix');
    this.hasSuffix = hasSlot(this.element, 'suffix');
  }
  connectedCallback() {
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleScrollLeft = this.handleScrollLeft.bind(this);
    this.handleScrollRight = this.handleScrollRight.bind(this);
    this.syncActiveTabIndicator = this.syncActiveTabIndicator.bind(this);
    this.updateSelectedTab = this.updateSelectedTab.bind(this);
    this.handleSlotChange = this.handleSlotChange.bind(this);
    this.handleNavSlotChange = this.handleNavSlotChange.bind(this);
    this.getAllTabs = this.getAllTabs.bind(this);
    this.getAllPanels = this.getAllPanels.bind(this);
    this.getActiveTab = this.getActiveTab.bind(this);
    this.getTabOfName = this.getTabOfName.bind(this);
  }
  componentDidLoad() {
    // Set initial tab state when the tabs first become visible
    const observer = new IntersectionObserver((entries, observer) => {
      if (entries[0].intersectionRatio > 0) {
        this.setAriaLabels();
        this.setActiveTab(this.getActiveTab() || this.getTabOfName(this.currentTab) || this.getAllTabs()[0], false);
        observer.unobserve(entries[0].target);
      }
    });
    observer.observe(this.element);
    focusVisible.observe(this.tabGroup);
    this.resizeObserver = new ResizeObserver(() => this.updateScrollControls());
    this.resizeObserver.observe(this.nav);
    requestAnimationFrame(() => this.updateScrollControls());
    // Update aria labels if the DOM changes
    this.mutationObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => {
        return !!mutation.attributeName && !['aria-labelledby', 'aria-controls'].includes(mutation.attributeName);
      })) {
        setTimeout(() => this.setAriaLabels());
      }
    });
    this.mutationObserver.observe(this.element, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  }
  disconnectedCallback() {
    this.mutationObserver.disconnect();
    focusVisible.unobserve(this.tabGroup);
    this.resizeObserver.unobserve(this.nav);
  }
  /** Shows the specified tab panel. */
  async show(panel) {
    const tab = this.getTabOfName(panel);
    if (tab) {
      this.setActiveTab(tab);
    }
  }
  getAllTabs(includeDisabled = false) {
    if (!this.tabs)
      return [];
    const slot = this.tabs.querySelector('slot');
    return extractElementsFromSlot(slot, 'tt-tab').filter((el) => includeDisabled || !el.disabled);
  }
  getAllPanels() {
    var _a;
    const slot = (_a = this.body) === null || _a === void 0 ? void 0 : _a.querySelector('slot');
    return extractElementsFromSlot(slot, 'tt-tab-panel');
  }
  getActiveTab() {
    return this.getAllTabs().find((el) => el.active);
  }
  getTabOfName(name) {
    return this.getAllTabs().find((el) => el.panel === name);
  }
  handleClick(event) {
    const target = event.target;
    const tab = target.closest('tt-tab');
    const tabGroup = tab === null || tab === void 0 ? void 0 : tab.closest('tt-tab-group');
    // Ensure the target tab is in this tab group
    if (tabGroup !== this.element) {
      return false;
    }
    if (tab) {
      this.setActiveTab(tab);
    }
  }
  handleKeyDown(event) {
    const target = event.target;
    const tab = target.closest('tt-tab');
    const tabGroup = tab === null || tab === void 0 ? void 0 : tab.closest('tt-tab-group');
    // Ensure the target tab is in this tab group
    if (tabGroup !== this.element) {
      return false;
    }
    // Activate a tab
    if (['Enter', ' '].includes(event.key)) {
      if (tab) {
        this.setActiveTab(tab);
        event.preventDefault();
      }
    }
    // Move focus left or right
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      const activeEl = document.activeElement;
      if (activeEl && activeEl.tagName.toLowerCase() === 'tt-tab') {
        const tabs = this.getAllTabs();
        if (!tabs)
          return;
        let index = tabs.indexOf(activeEl);
        if (event.key === 'Home') {
          index = 0;
        }
        else if (event.key === 'End') {
          index = tabs.length - 1;
        }
        else if (event.key === 'ArrowLeft') {
          index = Math.max(0, index - 1);
        }
        else if (event.key === 'ArrowRight') {
          index = Math.min(tabs.length - 1, index + 1);
        }
        tabs[index].setFocus({ preventScroll: true });
        if (['top', 'bottom'].includes(this.placement)) {
          scrollIntoView(tabs[index], this.nav, 'horizontal');
        }
        event.preventDefault();
      }
    }
  }
  handleScrollLeft() {
    this.nav.scroll({
      left: this.nav.scrollLeft - this.nav.clientWidth,
      behavior: 'smooth',
    });
  }
  handleScrollRight() {
    this.nav.scroll({
      left: this.nav.scrollLeft + this.nav.clientWidth,
      behavior: 'smooth',
    });
  }
  updateScrollControls() {
    if (this.noScrollControls) {
      this.hasScrollControls = false;
    }
    else {
      this.hasScrollControls = ['top', 'bottom'].includes(this.placement) && this.nav.scrollWidth > this.nav.clientWidth;
    }
  }
  updateSelectedTab() {
    this.setActiveTab(this.getTabOfName(this.currentTab), false);
  }
  setActiveTab(tab, emitEvents = true) {
    this.activeTab = this.getActiveTab() || this.activeTab;
    if (tab && tab !== this.activeTab && !tab.disabled) {
      const previousTab = this.activeTab;
      this.activeTab = tab;
      if (this.activeTab.panel) {
        this.currentTab = this.activeTab.panel;
      }
      // Sync tabs and panels
      this.getAllTabs().map((el) => (el.active = el === this.activeTab));
      this.getAllPanels().map((el) => (el.active = el.name === this.activeTab.panel));
      this.syncActiveTabIndicator();
      if (['top', 'bottom'].includes(this.placement)) {
        scrollIntoView(this.activeTab, this.nav, 'horizontal');
      }
      this.body.scrollTo(0, 0);
      // Emit events
      if (emitEvents) {
        if (previousTab) {
          this.slTabHide.emit({ name: previousTab.panel });
        }
        this.slTabShow.emit({ name: this.activeTab.panel });
      }
    }
  }
  setAriaLabels() {
    const tabs = this.getAllTabs();
    const panels = this.getAllPanels();
    // Link each tab with its corresponding panel
    tabs.map((tab) => {
      var _a, _b;
      const panel = panels.find((el) => el.name === tab.panel);
      if (panel) {
        tab.setAttribute('aria-controls', (_a = panel.getAttribute('id')) !== null && _a !== void 0 ? _a : '');
        panel.setAttribute('aria-labelledby', (_b = tab.getAttribute('id')) !== null && _b !== void 0 ? _b : '');
      }
    });
  }
  syncActiveTabIndicator() {
    const tab = this.getActiveTab();
    if (tab) {
      this.activeTabIndicator.style.display = 'block';
    }
    else {
      this.activeTabIndicator.style.display = 'none';
      return;
    }
    const width = tab.clientWidth;
    const height = tab.clientHeight;
    const offset = getOffset(tab, this.nav);
    const offsetTop = offset.top + this.nav.scrollTop;
    const offsetLeft = offset.left + this.nav.scrollLeft;
    switch (this.placement) {
      case 'top':
      case 'bottom':
        this.activeTabIndicator.style.width = `${width}px`;
        this.activeTabIndicator.style.height = '';
        this.activeTabIndicator.style.transform = `translateX(${offsetLeft}px)`;
        break;
      case 'left':
      case 'right':
        this.activeTabIndicator.style.width = '';
        this.activeTabIndicator.style.height = `${height}px`;
        this.activeTabIndicator.style.transform = `translateY(${offsetTop}px)`;
        break;
    }
  }
  handleNavSlotChange() {
    this.setActiveTab(this.getActiveTab() || this.getTabOfName(this.currentTab) || this.getAllTabs()[0], false);
  }
  render() {
    return (h("div", { part: "base", ref: (el) => {
        if (el)
          this.tabGroup = el;
      }, class: {
        'tab-group': true,
        'tab-subtabs': this.subtabs,
        'has-prefix': this.hasPrefix,
        'has-suffix': this.hasSuffix,
        // Placements
        'tab-group--top': this.placement === 'top',
        'tab-group--bottom': this.placement === 'bottom',
        'tab-group--left': this.placement === 'left',
        'tab-group--right': this.placement === 'right',
        'tab-group--has-scroll-controls': this.hasScrollControls,
      }, onClick: this.handleClick, onKeyDown: this.handleKeyDown }, h("div", { class: "tab-group__nav-container" }, this.hasScrollControls && (h("tt-icon-button", { label: "show more tabs left", class: "tab-group__scroll-button tab-group__scroll-button--left", exportparts: "base:scroll-button", library: "one", name: "chevron-left", "aria-hidden": "true", tabIndex: -1, onClick: this.handleScrollLeft })), h("div", { class: "tab-group__content" }, h("span", { part: "prefix", class: "tab-group__prefix" }, h("slot", { onSlotchange: this.handleSlotChange, name: "prefix" })), h("div", { ref: (el) => {
        if (el)
          this.nav = el;
      }, key: "nav", part: "nav", class: "tab-group__nav" }, h("div", { ref: (el) => {
        if (el)
          this.tabs = el;
      }, part: "tabs", class: "tab-group__tabs", role: "tablist" }, h("div", { ref: (el) => {
        if (el)
          this.activeTabIndicator = el;
      }, part: "active-tab-indicator", class: "tab-group__active-tab-indicator" }), h("slot", { name: "nav", onSlotchange: this.handleNavSlotChange }))), h("span", { part: "suffix", class: "tab-group__suffix" }, h("slot", { onSlotchange: this.handleSlotChange, name: "suffix" }))), this.hasScrollControls && (h("tt-icon-button", { label: "show more tabs right", class: "tab-group__scroll-button tab-group__scroll-button--right", exportparts: "base:scroll-button", library: "one", name: "chevron-right", "aria-hidden": "true", tabIndex: -1, onClick: this.handleScrollRight })), h("div", { class: "tab-group-border" })), h("div", { ref: (el) => {
        if (el)
          this.body = el;
      }, part: "body", class: "tab-group__body" }, h("slot", { onSlotchange: this.syncActiveTabIndicator }))));
  }
  get element() { return this; }
  static get watchers() { return {
    "placement": ["handlePlacementChange"],
    "noScrollControls": ["handleNoScrollControlsChange"],
    "currentTab": ["updateSelectedTab"]
  }; }
  static get style() { return tabGroupCss; }
}, [1, "tt-tab-group", {
    "placement": [1],
    "subtabs": [516],
    "currentTab": [1537, "current-tab"],
    "noScrollControls": [4, "no-scroll-controls"],
    "hasScrollControls": [32],
    "hasPrefix": [32],
    "hasSuffix": [32],
    "show": [64]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["tt-tab-group", "tt-icon", "tt-icon-button"];
  components.forEach(tagName => { switch (tagName) {
    case "tt-tab-group":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, TabGroup);
      }
      break;
    case "tt-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "tt-icon-button":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const TtTabGroup = TabGroup;
const defineCustomElement = defineCustomElement$1;

export { TtTabGroup, defineCustomElement };
